/*
 * BajadaRenta.jsx — módulo comercial de renegociación de arrendamientos.
 *
 * Vistas:
 *  - KPI Dashboard (ahorro anual firmado, potencial, tasa éxito, ticket medio)
 *  - Candidatos (sites con potencial detectados por el sistema)
 *  - Negociaciones activas + histórico
 *  - Detalle: propuesta IA + estrategias normativas aplicables + audit log
 */
import React, { useState, useEffect, useMemo } from 'react';
import api from '../api/client';
import { toast } from 'sonner';
import {
  TrendingDown, Sparkles, FileText, Loader2, Handshake, CheckCircle,
  XCircle, Clock, Building, Wrench, ChevronRight, PieChart, ScrollText,
  Hammer, Plus, Trash2, Save, Gavel, AlertTriangle, Eye, Copy, Send, Printer,
  FileDown, Presentation,
} from 'lucide-react';
import BajadaRentaDossier from './BajadaRentaDossier';
import PresentacionBajadaRenta from './PresentacionBajadaRenta';
import DuplicarNegociacionModal from './DuplicarNegociacionModal';
import { suggestVisit } from '../utils/annualVisitPlanner';

const STATUS_META = {
  identified:       { label: 'Identificado',        color: '#94a3b8', icon: Clock },
  visit_scheduled:  { label: 'Visita programada',   color: '#60a5fa', icon: Wrench },
  visit_done:       { label: 'Visita realizada',    color: '#818cf8', icon: CheckCircle },
  proposal_sent:    { label: 'Propuesta enviada',   color: '#fbbf24', icon: FileText },
  in_negotiation:   { label: 'En negociación',      color: '#fb923c', icon: Handshake },
  accepted:         { label: 'Aceptada',            color: '#22c55e', icon: CheckCircle },
  rejected:         { label: 'Rechazada',           color: '#ef4444', icon: XCircle },
  signed:           { label: 'Firmada',             color: '#A3FF12', icon: CheckCircle },
};

const StatusPill = ({ status }) => {
  const meta = STATUS_META[status] || STATUS_META.identified;
  const Icon = meta.icon;
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-black tracking-wide"
      style={{ backgroundColor: `${meta.color}20`, color: meta.color, border: `1px solid ${meta.color}40` }}>
      <Icon size={10}/> {meta.label.toUpperCase()}
    </span>
  );
};

// Backend guarda las rentas como mensuales (`*_monthly`). En UI SIEMPRE trabajamos
// con importes anuales: multiplicamos al leer, dividimos al escribir.
const toAnnual = (m) => (Number(m) || 0) * 12;
const fromAnnual = (a) => Math.round(((Number(a) || 0) / 12) * 100) / 100;
const eur0 = (v) => (Number(v) || 0).toLocaleString('es-ES', { maximumFractionDigits: 0 });

const BajadaRenta = () => {
  const [kpi, setKpi] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [negotiations, setNegotiations] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedNeg, setSelectedNeg] = useState(null);
  const [tab, setTab] = useState('candidatos');
  const [showManualModal, setShowManualModal] = useState(false);
  const [showPresentation, setShowPresentation] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      // Los 3 endpoints "ligeros" son los que bloquean el render inicial.
      // /api/sites (21MB, 11k rows) se carga en background y no bloquea la UI:
      // sólo se usa dentro del modal de "Nueva bajada" para el buscador de sites.
      const [k, c, n] = await Promise.all([
        api.get('/rent-reduction/kpi').then((r) => r.data),
        api.get('/rent-reduction/suggestions/candidates').then((r) => r.data).catch(() => []),
        api.get('/rent-reduction').then((r) => r.data),
      ]);
      setKpi(k); setCandidates(c); setNegotiations(n);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo cargar el módulo');
    } finally { setLoading(false); }
    // Carga diferida de sites: no bloquea, se actualiza cuando llega.
    api.get('/sites/lite').then((r) => setSites(r.data || [])).catch(() => {});
  };
  useEffect(() => { refresh(); }, []);

  const createFromCandidate = async (c) => {
    try {
      const { data } = await api.post('/rent-reduction', {
        site_id: c.site_id,
        current_rent_monthly: c.current_rent_monthly,
        target_rent_monthly: c.suggested_target_monthly,
        contract_end_date: c.contract_end,
        owner_name: c.owner_name || '',
        note: `Ahorro potencial estimado: ${c.potential_annual_saving_eur} €/año`,
      });
      toast.success('Negociación iniciada');
      await refresh();
      setSelectedNeg(data);
      setTab('activas');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error creando negociación');
    }
  };

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto" data-testid="rent-reduction-page">
      <div className="mb-4 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] font-black tracking-widest text-[#A3FF12]">MÓDULO COMERCIAL</div>
          <h1 className="text-2xl sm:text-3xl font-black text-white mt-1 flex items-center gap-2">
            <TrendingDown size={26}/> Bajada de renta
          </h1>
          <p className="text-white/50 text-sm mt-1">Detecta candidatos, genera propuestas con Adquiriam que aprovechan la normativa vigente, y cierra ahorros contabilizados.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowPresentation(true)} data-testid="btn-open-presentation"
            className="flex items-center gap-2 bg-white/10 border border-white/20 text-white font-black px-4 py-2 rounded-xl hover:bg-white/20 text-sm">
            <Presentation size={16}/> Presentación proyecto
          </button>
          <button onClick={() => setShowManualModal(true)} data-testid="btn-new-manual-negotiation"
            className="flex items-center gap-2 bg-[#A3FF12] text-black font-black px-4 py-2 rounded-xl hover:bg-[#b8ff3a] text-sm">
            <Plus size={16}/> Nueva bajada
          </button>
        </div>
      </div>

      {kpi && <KpiRow kpi={kpi}/>}

      <div className="mt-6 flex items-center gap-2 border-b border-white/10">
        {[
          { id: 'candidatos', label: `Candidatos (${candidates.length})` },
          { id: 'activas', label: `Negociaciones (${negotiations.filter(n => n.status !== 'signed' && n.status !== 'rejected').length})` },
          { id: 'historico', label: `Histórico (${negotiations.filter(n => n.status === 'signed' || n.status === 'rejected').length})` },
        ].map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)}
            data-testid={`tab-${t.id}`}
            className={`text-xs font-bold px-3 py-2 -mb-px border-b-2 transition-colors ${tab === t.id ? 'text-[#A3FF12] border-[#A3FF12]' : 'text-white/50 border-transparent hover:text-white/80'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {loading && <div className="py-12 flex justify-center"><Loader2 className="animate-spin text-[#A3FF12]" size={28}/></div>}

      {!loading && tab === 'candidatos' && (
        <CandidatesList items={candidates} onStart={createFromCandidate}/>
      )}
      {!loading && tab === 'activas' && (
        <NegotiationsList items={negotiations.filter(n => n.status !== 'signed' && n.status !== 'rejected')}
                          onOpen={setSelectedNeg}
                          onDeleted={() => refresh()}/>
      )}
      {!loading && tab === 'historico' && (
        <NegotiationsList items={negotiations.filter(n => n.status === 'signed' || n.status === 'rejected')}
                          onOpen={setSelectedNeg}
                          onDeleted={() => refresh()}/>
      )}

      {selectedNeg && (
        <NegotiationDetail neg={selectedNeg} sites={sites} onClose={() => setSelectedNeg(null)} onUpdated={(n) => {
          setSelectedNeg(n); refresh();
        }}/>
      )}

      {showManualModal && (
        <ManualNegotiationModal
          sites={sites}
          onClose={() => setShowManualModal(false)}
          onCreated={async (neg) => {
            setShowManualModal(false);
            await refresh();
            setSelectedNeg(neg);
            setTab('activas');
          }}
        />
      )}
      {showPresentation && (
        <PresentacionBajadaRenta
          negotiations={negotiations}
          sites={sites}
          kpi={kpi}
          candidates={candidates}
          onClose={() => setShowPresentation(false)}
        />
      )}
    </div>
  );
};

const KpiRow = ({ kpi }) => (
  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
    <KpiCard label="Ahorro firmado / año" value={`${(kpi.savings_annual_signed_eur || 0).toLocaleString('es-ES')} €`} accent/>
    <KpiCard label="Ahorro potencial abierto" value={`${(kpi.savings_potential_annual_eur || 0).toLocaleString('es-ES')} €`}/>
    <KpiCard label="Tasa de éxito" value={`${(kpi.success_rate * 100).toLocaleString('es-ES', {maximumFractionDigits:0})}%`}/>
    <KpiCard label="Rebaja media" value={`${kpi.avg_reduction_pct.toLocaleString('es-ES', {minimumFractionDigits:1, maximumFractionDigits:1})}%`}/>
  </div>
);

const KpiCard = ({ label, value, accent }) => (
  <div className={`p-4 rounded-xl border ${accent ? 'bg-[#A3FF12]/10 border-[#A3FF12]/30' : 'bg-white/5 border-white/10'}`}>
    <div className="text-[10px] uppercase text-white/50 font-bold tracking-wide">{label}</div>
    <div className={`text-2xl sm:text-3xl font-black mt-1 ${accent ? 'text-[#A3FF12]' : 'text-white'}`}>{value}</div>
  </div>
);

const CandidatesList = ({ items, onStart }) => (
  <div className="mt-4 space-y-2" data-testid="candidates-list">
    {items.length === 0 && (
      <div className="p-8 text-center text-white/40 text-sm border border-white/10 rounded-xl bg-white/5">
        Sin candidatos auto-detectados. Necesitas al menos <b>renta &gt; percentil 60</b> del portfolio y sin negociación abierta.
        <div className="text-white/60 text-xs mt-2">
          Puedes lanzar una bajada sobre <b>cualquier emplazamiento</b> con el botón <b className="text-[#A3FF12]">"Nueva bajada"</b> arriba a la derecha.
        </div>
      </div>
    )}
    {items.map((c) => (
      <div key={c.site_id} className="bg-white/5 border border-white/10 rounded-xl p-4 flex items-center justify-between gap-3 hover:bg-white/10 transition-colors">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-white font-black text-sm">{c.site_id}</span>
            {c.urgency >= 2 && <span className="text-[10px] font-black px-2 py-0.5 rounded bg-red-500/20 text-red-300">URGENTE · contrato próximo</span>}
            {c.urgency === 1 && <span className="text-[10px] font-black px-2 py-0.5 rounded bg-orange-500/20 text-orange-300">MEDIO PLAZO</span>}
          </div>
          <div className="text-white/60 text-xs mt-0.5 truncate">{c.address}</div>
          <div className="text-white/80 text-xs mt-1">
            {eur0(toAnnual(c.current_rent_monthly))} €/año → objetivo {eur0(toAnnual(c.suggested_target_monthly))} €/año
            <span className="text-[#A3FF12] font-bold ml-2">Ahorro: {eur0(c.potential_annual_saving_eur)} €/año</span>
          </div>
        </div>
        <button onClick={() => onStart(c)}
          data-testid={`start-neg-${c.site_id}`}
          className="bg-[#A3FF12] text-black font-black text-xs px-3 py-2 rounded-lg hover:bg-[#b8ff3a] flex items-center gap-1">
          <Handshake size={12}/> Iniciar
        </button>
      </div>
    ))}
  </div>
);

const NegotiationsList = ({ items, onOpen, onDeleted }) => {
  const del = async (e, n) => {
    e.stopPropagation();
    if (!window.confirm(`¿Eliminar bajada de renta de ${n.site_id || n.id}?`)) return;
    try {
      await api.delete(`/rent-reduction/${n.id}`);
      toast.success('Bajada eliminada');
      onDeleted?.(n.id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al eliminar');
    }
  };
  return (
    <div className="mt-4 space-y-2" data-testid="negotiations-list">
      {items.length === 0 && <div className="p-8 text-center text-white/40 text-sm">Sin negociaciones en este estado.</div>}
      {items.map((n) => (
        <div key={n.id} className="w-full bg-white/5 border border-white/10 rounded-xl hover:bg-white/10 transition-colors">
          <div className="flex items-stretch">
            <button onClick={() => onOpen(n)} data-testid={`open-neg-${n.id}`}
              className="flex-1 text-left p-4 min-w-0">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-white font-black text-sm">{n.site_id}</span>
                    <StatusPill status={n.status}/>
                    {(() => {
                      const curA = toAnnual(n.current_rent_monthly);
                      const finA = toAnnual(n.final_rent_monthly ?? n.target_rent_monthly);
                      if (!curA || !finA || finA >= curA) return null;
                      const annualSaving = curA - finA;
                      const pct = ((curA - finA) / curA) * 100;
                      return (
                        <span className="text-[10px] font-black bg-[#A3FF12]/15 text-[#A3FF12] border border-[#A3FF12]/30 px-2 py-0.5 rounded"
                          title={`Ahorro anual (${n.final_rent_monthly ? 'firmada' : 'objetivo'})`}>
                          -{pct.toLocaleString('es-ES', {maximumFractionDigits:0})}% · {eur0(annualSaving)} €/año
                        </span>
                      );
                    })()}
                  </div>
                  <div className="text-white/60 text-xs mt-1">
                    Actual: {eur0(toAnnual(n.current_rent_monthly))} €/año · Objetivo: {eur0(toAnnual(n.target_rent_monthly))} €/año
                    {n.final_rent_monthly && <span className="text-[#A3FF12] font-bold ml-2">Firmado: {eur0(toAnnual(n.final_rent_monthly))} €/año</span>}
                  </div>
                </div>
                <ChevronRight className="text-white/40 flex-shrink-0" size={18}/>
              </div>
            </button>
            <button onClick={(e) => del(e, n)} data-testid={`delete-neg-${n.id}`}
              title="Eliminar bajada"
              className="px-3 flex items-center text-white/30 hover:text-red-400 hover:bg-red-500/10 border-l border-white/5">
              <Trash2 size={16}/>
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

// ============ Modal: crear negociación manualmente eligiendo emplazamiento ============
const ManualNegotiationModal = ({ sites, onClose, onCreated }) => {
  const [siteId, setSiteId] = useState('');
  const [currentRent, setCurrentRent] = useState('');
  const [targetPct, setTargetPct] = useState(10);
  const [contractEnd, setContractEnd] = useState('');
  const [ownerName, setOwnerName] = useState('');
  const [ownerContact, setOwnerContact] = useState('');
  const [note, setNote] = useState('');
  const [siteSearch, setSiteSearch] = useState('');
  const [saving, setSaving] = useState(false);

  // Pre-fill al elegir un site: renta actual (ANUAL), propietario, contrato
  useEffect(() => {
    if (!siteId || siteId.startsWith('__manual__')) return;
    const s = sites.find((x) => x.id === siteId);
    if (!s) return;
    // sites.current_rent está en €/año → lo usamos tal cual.
    if (s.current_rent && !currentRent) setCurrentRent(String(Number(s.current_rent) || 0));
    if (s.owner_name && !ownerName) setOwnerName(s.owner_name);
    if (s.owner_phone && !ownerContact) setOwnerContact(s.owner_phone);
    if (s.contract_end && !contractEnd) setContractEnd(String(s.contract_end).slice(0, 10));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId]);

  const filteredSites = useMemo(() => {
    if (!siteSearch) return sites;
    const q = siteSearch.toLowerCase();
    return sites.filter((s) =>
      (s.name || '').toLowerCase().includes(q)
      || (s.code || '').toLowerCase().includes(q)
      || (s.address || '').toLowerCase().includes(q)
      || (s.municipality || '').toLowerCase().includes(q));
  }, [sites, siteSearch]);

  const currentAnnual = Number(currentRent) || 0; // renta actual ANUAL (input directo)
  const targetAnnual = currentAnnual * (1 - Number(targetPct) / 100);
  const annualSaving = currentAnnual - targetAnnual;

  const submit = async () => {
    if (!siteId) { toast.error('Selecciona un emplazamiento'); return; }
    if (!currentAnnual || currentAnnual <= 0) { toast.error('Introduce la renta actual (>0)'); return; }
    let siteRef;
    if (siteId.startsWith('__manual__')) {
      siteRef = siteId.replace('__manual__', '');
    } else {
      const site = sites.find((x) => x.id === siteId);
      siteRef = site?.code || site?.name || siteId;
    }
    setSaving(true);
    try {
      const { data } = await api.post('/rent-reduction', {
        site_id: siteRef,
        // El backend guarda mensual internamente; convertimos aquí.
        current_rent_monthly: fromAnnual(currentAnnual),
        target_rent_monthly: fromAnnual(targetAnnual),
        contract_end_date: contractEnd || null,
        owner_name: ownerName || '',
        owner_contact: ownerContact || '',
        note: note || `Bajada manual del ${targetPct}% sobre ${eur0(currentAnnual)} €/año`,
      });
      toast.success('Negociación creada');
      onCreated(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error creando negociación');
    } finally { setSaving(false); }
  };

  // Confirmación al cerrar si hay datos escritos (evita pérdidas accidentales).
  const hasContent = !!(siteId || currentRent || ownerName || ownerContact || note);
  const confirmClose = () => {
    if (!hasContent || window.confirm('Estás creando una negociación. ¿Descartar los cambios?')) {
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-[10050] bg-black/85 backdrop-blur flex items-end sm:items-center justify-center p-0 sm:p-4"
      data-testid="manual-negotiation-modal">
      <div className="bg-[#0a0a0a] border border-[#A3FF12]/30 rounded-t-2xl sm:rounded-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto">
        <div className="p-5 border-b border-white/10 flex items-center justify-between gap-3 sticky top-0 bg-[#0a0a0a] z-10">
          <div>
            <div className="text-[10px] font-black tracking-widest text-[#A3FF12]">NUEVA NEGOCIACIÓN</div>
            <h3 className="text-lg font-black text-white flex items-center gap-2 mt-1">
              <Handshake size={18} className="text-[#A3FF12]"/> Iniciar bajada de renta
            </h3>
            <p className="text-white/50 text-xs mt-1">Elige cualquier emplazamiento del portfolio para arrancar una negociación con estrategias y palancas.</p>
          </div>
          <button onClick={confirmClose} data-testid="btn-close-modal" className="text-white/40 hover:text-white"><XCircle size={22}/></button>
        </div>

        <div className="p-5 space-y-4">
          {/* Site picker */}
          <div>
            <div className="text-xs uppercase text-white/60 mb-1">Emplazamiento *</div>
            {siteId ? (
              (() => {
                const isManual = siteId.startsWith('__manual__');
                const manualCode = isManual ? siteId.replace('__manual__', '') : null;
                const s = isManual ? null : sites.find((x) => x.id === siteId);
                return (
                  <div className={`flex items-center justify-between gap-3 rounded-lg px-3 py-2.5 ${isManual ? 'bg-orange-500/10 border border-orange-500/40' : 'bg-[#A3FF12]/10 border border-[#A3FF12]/40'}`} data-testid="selected-site-chip">
                    <div className="min-w-0">
                      <div className="text-white font-bold text-sm truncate">
                        {isManual ? manualCode : s?.name}
                        {!isManual && s?.code && <span className="text-white/50 font-normal text-xs"> · {s.code}</span>}
                        {isManual && <span className="text-orange-300 font-normal text-[10px] ml-2">CÓDIGO MANUAL</span>}
                      </div>
                      <div className="text-white/50 text-xs truncate">
                        {isManual ? 'No está aún en el portfolio — se registrará solo en la negociación' : (s?.address || s?.municipality || '')}
                      </div>
                    </div>
                    <button type="button" onClick={() => { setSiteId(''); setSiteSearch(''); }}
                      data-testid="btn-change-site"
                      className={`text-xs hover:underline flex-shrink-0 ${isManual ? 'text-orange-300' : 'text-[#A3FF12]'}`}>Cambiar</button>
                  </div>
                );
              })()
            ) : (
              <>
                <input value={siteSearch} onChange={(e) => setSiteSearch(e.target.value)}
                  placeholder="Buscar por nombre, código, dirección o municipio… o escribe un código nuevo"
                  data-testid="site-search-input"
                  autoFocus
                  className="w-full bg-black/40 border border-white/10 focus:border-[#A3FF12]/50 rounded-lg px-3 py-2 text-white text-sm outline-none"/>
                <div className="mt-1 max-h-56 overflow-y-auto border border-white/10 rounded-lg bg-black/30 divide-y divide-white/5">
                  {filteredSites.length === 0 ? (
                    <div className="p-3 text-white/40 text-xs text-center">
                      {sites.length === 0 ? 'Sin sites en portfolio. Da de alta uno en Prospección primero.' : 'Ningún site coincide con la búsqueda.'}
                    </div>
                  ) : (
                    filteredSites.slice(0, 40).map((s) => (
                      <button type="button" key={s.id} onClick={() => setSiteId(s.id)}
                        data-testid={`select-site-${s.id}`}
                        className="w-full text-left px-3 py-2 text-xs hover:bg-[#A3FF12]/10 hover:text-[#A3FF12] text-white/80 transition-colors">
                        <div className="font-bold flex items-center gap-2">
                          <span className="truncate">{s.name}</span>
                          {s.code && <span className="text-white/40 font-normal">· {s.code}</span>}
                          {s.current_rent > 0 && (
                            <span className="ml-auto text-[10px] text-[#A3FF12]/80 font-mono">{new Intl.NumberFormat('es-ES').format(s.current_rent)} €/año</span>
                          )}
                        </div>
                        <div className="text-white/40 text-[10px] truncate mt-0.5">{s.address || s.municipality || '—'}</div>
                      </button>
                    ))
                  )}
                  {filteredSites.length > 40 && (
                    <div className="px-3 py-1.5 text-[10px] text-white/40 text-center">Mostrando 40 de {filteredSites.length} · afina la búsqueda</div>
                  )}
                </div>
                {/* Opción manual: usar el texto tal cual como código si no está en el portfolio */}
                {siteSearch.trim().length >= 2 && !filteredSites.some((s) => (s.code || '').toLowerCase() === siteSearch.trim().toLowerCase() || (s.name || '').toLowerCase() === siteSearch.trim().toLowerCase()) && (
                  <button type="button" onClick={() => setSiteId(`__manual__${siteSearch.trim()}`)}
                    data-testid="btn-use-manual-code"
                    className="w-full mt-1 border border-dashed border-orange-500/40 hover:bg-orange-500/10 text-orange-300 rounded-lg px-3 py-2 text-xs flex items-center gap-2">
                    <Plus size={12}/> Usar <b className="text-orange-200">"{siteSearch.trim()}"</b> como código manual (no está en el portfolio)
                  </button>
                )}
              </>
            )}
          </div>

          {/* Rent details */}
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs uppercase text-white/60">Renta actual (€/año) *</span>
              <input type="number" value={currentRent} onChange={(e) => setCurrentRent(e.target.value)}
                data-testid="input-current-rent"
                className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 mt-1 text-white"/>
            </label>
            <label className="block">
              <span className="text-xs uppercase text-white/60">Objetivo bajada (%)</span>
              <input type="number" min="1" max="80" value={targetPct} onChange={(e) => setTargetPct(e.target.value)}
                data-testid="input-target-pct"
                className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 mt-1 text-white"/>
              <span className="text-[10px] text-white/40 mt-0.5 block">
                = {eur0(targetAnnual)} €/año · ahorro {eur0(annualSaving)} €/año
              </span>
            </label>
          </div>

          <label className="block">
            <span className="text-xs uppercase text-white/60">Fin de contrato (opcional)</span>
            <input type="date" value={contractEnd} onChange={(e) => setContractEnd(e.target.value)}
              className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 mt-1 text-white"/>
          </label>

          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs uppercase text-white/60">Propietario</span>
              <input value={ownerName} onChange={(e) => setOwnerName(e.target.value)}
                data-testid="input-owner-name"
                className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 mt-1 text-white"/>
            </label>
            <label className="block">
              <span className="text-xs uppercase text-white/60">Contacto (email/tel)</span>
              <input value={ownerContact} onChange={(e) => setOwnerContact(e.target.value)}
                className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 mt-1 text-white"/>
            </label>
          </div>

          <label className="block">
            <span className="text-xs uppercase text-white/60">Nota inicial (opcional)</span>
            <textarea rows="2" value={note} onChange={(e) => setNote(e.target.value)}
              placeholder="Contexto, historia, quejas recientes, palancas conocidas…"
              className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 mt-1 text-white text-sm"/>
          </label>
        </div>

        <div className="p-5 border-t border-white/10 flex justify-end gap-2 sticky bottom-0 bg-[#0a0a0a]">
          <button onClick={confirmClose} className="px-4 py-2 border border-white/10 rounded-xl text-white/70 text-sm">Cancelar</button>
          <button onClick={submit} disabled={saving || !siteId || !currentAnnual}
            data-testid="btn-save-manual-neg"
            className="bg-[#A3FF12] text-black font-black px-4 py-2 rounded-xl hover:bg-[#b8ff3a] disabled:opacity-40 flex items-center gap-2">
            {saving ? <Loader2 className="animate-spin" size={14}/> : <Handshake size={14}/>}
            {saving ? 'Creando…' : 'Iniciar negociación'}
          </button>
        </div>
      </div>
    </div>
  );
};


// Editor rápido del % objetivo de bajada — persistido en backend
const TargetPctEditor = ({ neg, onUpdated }) => {
  const cur = Number(neg.current_rent_monthly) || 0;
  const target = Number(neg.target_rent_monthly) || 0;
  const currentPct = cur > 0 ? ((cur - target) / cur * 100) : 0;
  const [pct, setPct] = useState(currentPct.toFixed(1));
  const [saving, setSaving] = useState(false);
  useEffect(() => { setPct(currentPct.toFixed(1)); }, [currentPct]);
  const newTarget = cur * (1 - (Number(pct) || 0) / 100);
  const dirty = Math.abs(newTarget - target) > 0.005;

  const save = async () => {
    if (!dirty) return;
    setSaving(true);
    try {
      const { data } = await api.patch(`/rent-reduction/${neg.id}`, {
        target_rent_monthly: Math.round(newTarget * 100) / 100,
      });
      toast.success(`Objetivo actualizado a ${eur0(newTarget * 12)} €/año`);
      onUpdated(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error guardando objetivo');
    } finally { setSaving(false); }
  };

  return (
    <div className="bg-white/5 border border-white/10 rounded-xl p-3 flex flex-col sm:flex-row items-center gap-3" data-testid="target-pct-editor">
      <div className="text-white/60 text-xs font-bold uppercase tracking-wide whitespace-nowrap">
        Objetivo de bajada
      </div>
      <div className="flex-1 flex items-center gap-2 w-full">
        <input type="range" min="0" max="60" step="0.5" value={pct}
          onChange={(e) => setPct(e.target.value)}
          data-testid="target-pct-slider"
          className="flex-1 accent-[#A3FF12]"/>
        <div className="flex items-center gap-1">
          <input type="number" min="0" max="80" step="0.1" value={pct}
            onChange={(e) => setPct(e.target.value)}
            data-testid="target-pct-input"
            className="w-16 bg-black/40 border border-white/10 rounded px-2 py-1 text-white text-sm text-right"/>
          <span className="text-white/60 text-sm">%</span>
        </div>
        <div className="text-[11px] text-white/70 whitespace-nowrap font-mono">
          → {Math.round(newTarget).toLocaleString('es-ES')} €/mes
          <span className="text-white/40"> · {Math.round(newTarget * 12).toLocaleString('es-ES')} €/año</span>
        </div>
      </div>
      <button onClick={save} disabled={!dirty || saving}
        data-testid="btn-save-target-pct"
        className="bg-[#A3FF12] text-black font-black text-xs px-3 py-2 rounded-lg hover:bg-[#b8ff3a] disabled:opacity-30 disabled:cursor-not-allowed flex items-center gap-1 whitespace-nowrap">
        {saving ? <Loader2 className="animate-spin" size={12}/> : <Save size={12}/>}
        {dirty ? 'Guardar' : 'Guardado'}
      </button>
    </div>
  );
};


// ============ Explicación de la lógica de negocio (business narrative) ============
// Traduce los números crudos de la negociación en una historia clara que el
// equipo puede leer y usar como argumento con el operador y con el propietario.
// Aparece siempre encima del panel de KPIs de la negociación.
const BusinessLogicPanel = ({ neg, sites }) => {
  const cur = Number(neg.current_rent_monthly) || 0;
  const fin = Number(neg.final_rent_monthly ?? neg.target_rent_monthly) || 0;
  if (!cur || !fin) return null;

  const annualCurrent = cur * 12;
  const annualFinal = fin * 12;
  const annualSaving = Math.max(0, annualCurrent - annualFinal);
  const bajadaPct = annualCurrent > 0 ? (annualSaving / annualCurrent) * 100 : 0;

  // Coste anual servicios in-kind (repartido por frecuencia).
  const items = neg.service_offer?.items || [];
  const servicesAnnual = items.reduce((s, it) => {
    const val = Number(it.market_value_eur) || 0;
    const freqYears = (Number(it.frequency_months) || 12) / 12;
    return s + (freqYears > 0 ? val / freqYears : 0);
  }, 0);
  const netAnnual = annualSaving - servicesAnnual;

  const eur = (v) => (Number(v) || 0).toLocaleString('es-ES', { maximumFractionDigits: 0 });

  // Recomendación de visita anual según meteorología de la provincia.
  const site = (sites || []).find((s) => s.code === neg.site_id || s.id === neg.site_id);
  const visit = site ? suggestVisit(site.province) : null;

  return (
    <div className="bg-gradient-to-r from-[#A3FF12]/8 via-white/5 to-blue-500/8 border border-white/15 rounded-xl p-4" data-testid="business-logic-panel">
      <div className="flex items-center gap-2 mb-3">
        <span className="w-1.5 h-1.5 rounded-full bg-[#A3FF12]"/>
        <span className="text-[10px] uppercase tracking-widest text-[#A3FF12] font-black">Lógica de negocio</span>
      </div>
      <p className="text-white text-sm leading-relaxed">
        Proponemos una <b className="text-[#A3FF12]">bajada de renta entre el 8% y el 10%</b> y,
        a cambio, TOTEM se ocupa de{' '}
        <b className="text-white">prevenir y reparar los deterioros que la instalación</b> genera
        sobre el inmueble (cubiertas, impermeabilizaciones, patologías inducidas por el
        emplazamiento, etc.).
      </p>
      <p className="text-white/85 text-sm leading-relaxed mt-2">
        Realizando <b className="text-white">visitas concertadas con la Propiedad</b>,
        reforzamos la confianza y consolidamos la relación, generando un
        <b className="text-white"> ahorro sustancial en la renta</b> y, en consecuencia a largo
        plazo, <b className="text-white">minorizando el riesgo de repliegue</b> al vencimiento
        del contrato.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mt-3">
        <BLBox label="TOTEM ahorra (renta)" value={`${eur(annualSaving)} €/año`} color="green" sign="+"/>
        <BLBox label="TOTEM paga (conservación preventiva)" value={`${eur(servicesAnnual)} €/año`} color="orange" sign="−"/>
        <BLBox label="Neto TOTEM · Ahorro real anual" value={`${eur(netAnnual)} €/año`} color={netAnnual >= 0 ? 'accent' : 'red'} sign={netAnnual >= 0 ? '=+' : '=−'} bold/>
      </div>
      <p className="text-white/70 text-xs mt-3 leading-relaxed">
        La Propiedad recibe una <b className="text-white">garantía real</b> de que los desperfectos
        generados por la instalación — humedades, tejados, anclajes — se resuelven sin coste para el
        cliente. TOTEM, aunque baja la renta y paga la conservación preventiva, sigue gastando
        <b className="text-white"> menos en neto</b> que la renta original: la diferencia entre la
        bajada y el coste de las intervenciones es{' '}
        <b className={netAnnual >= 0 ? 'text-[#A3FF12]' : 'text-red-400'}>
          {netAnnual >= 0 ? 'beneficio anual recurrente' : 'coste adicional — revisar oferta'}
        </b>. Multiplicado por los años restantes del contrato, es el ahorro estructural del contrato.
      </p>
      <p className="text-white/70 text-xs mt-2 leading-relaxed">
        Y sobre todo: al llevar a cabo un <b className="text-white">seguimiento anual con la
        Propiedad</b>, establecemos un vínculo directo que mejora la relación y
        <b className="text-white"> minimiza las posibilidades de repliegue</b> al vencimiento del
        contrato.
      </p>
      {visit && (
        <div className="mt-3 p-3 rounded-lg border border-white/15 bg-black/40" data-testid="visit-recommendation">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-14 h-14 rounded-lg bg-[#A3FF12]/15 border border-[#A3FF12]/40 flex flex-col items-center justify-center">
              <div className="text-[9px] uppercase tracking-widest text-[#A3FF12] font-black leading-none">
                {visit.date.toLocaleDateString('es-ES', { month: 'short' })}
              </div>
              <div className="text-white text-xl font-black leading-none mt-1">
                {visit.date.getUTCDate()}
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-widest text-[#A3FF12] font-black">
                Visita anual recomendada · clima {visit.zone.label.toLowerCase()}
              </div>
              <div className="text-white text-sm font-bold mt-0.5">
                {visit.dateLabel}
                <span className="text-white/50 font-normal ml-2 text-xs">
                  ({visit.daysFromNow >= 0 ? `en ${visit.daysFromNow} días` : `hace ${-visit.daysFromNow} días`})
                </span>
              </div>
              <div className="text-white/60 text-xs mt-1 leading-relaxed">{visit.zone.rationale}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const BLBox = ({ label, value, color, sign, bold }) => {
  const map = {
    green:  { text: 'text-[#A3FF12]', bg: 'bg-[#A3FF12]/10 border-[#A3FF12]/30' },
    orange: { text: 'text-orange-300', bg: 'bg-orange-500/10 border-orange-500/30' },
    accent: { text: 'text-[#A3FF12]', bg: 'bg-[#A3FF12]/20 border-[#A3FF12]/60' },
    red:    { text: 'text-red-400', bg: 'bg-red-500/10 border-red-500/40' },
  };
  const c = map[color] || map.green;
  return (
    <div className={`border rounded-lg p-2.5 ${c.bg}`}>
      <div className="text-[9px] uppercase tracking-widest text-white/70 font-black">{label}</div>
      <div className={`text-lg mt-0.5 flex items-baseline gap-1 ${c.text} ${bold ? 'font-black' : 'font-bold'}`}>
        <span className="text-xs opacity-70">{sign}</span>
        {value}
      </div>
    </div>
  );
};

// ============ Resumen consolidado de costes y beneficios ============
// Muestra en un vistazo lo que TOTEM ahorra, lo que se compromete a abonar
// en servicios in-kind, el neto para TOTEM y el valor total que recibe el
// propietario. Se calcula hasta fin de contrato si existe fecha; si no,
// se usa la duración del acuerdo definida en la oferta de servicios.
const CostBenefitSummary = ({ neg }) => {
  const cur = Number(neg.current_rent_monthly) || 0;
  const fin = Number(neg.final_rent_monthly ?? neg.target_rent_monthly) || 0;
  if (!cur || !fin) return null;

  const annualCurrent = cur * 12;
  const annualFinal = fin * 12;
  const annualSaving = Math.max(0, annualCurrent - annualFinal);

  // Años hasta fin de contrato (o duración definida en la oferta).
  let years = Number(neg.service_offer?.duration_years) || 1;
  if (neg.contract_end_date) {
    const end = new Date(neg.contract_end_date);
    if (!isNaN(end.getTime())) {
      years = Math.max(0, (end.getTime() - Date.now()) / (365.25 * 24 * 60 * 60 * 1000));
    }
  }

  // Coste total de los servicios in-kind hasta fin de contrato — respeta la
  // frecuencia real de cada servicio (los plurianuales sólo cuentan las
  // ejecuciones que caben en el periodo).
  const items = neg.service_offer?.items || [];
  const servicesTotal = items.reduce((sum, it) => {
    const val = Number(it.market_value_eur) || 0;
    const freqYears = (Number(it.frequency_months) || 12) / 12;
    if (freqYears <= 0) return sum;
    const execs = Math.floor(years / freqYears) + (years % freqYears >= freqYears / 2 ? 1 : 0);
    return sum + val * execs;
  }, 0);
  const servicesAnnual = items.reduce((sum, it) => {
    const val = Number(it.market_value_eur) || 0;
    const freqYears = (Number(it.frequency_months) || 12) / 12;
    return sum + (freqYears > 0 ? val / freqYears : 0);
  }, 0);

  const totalRentSaved = annualSaving * years;
  const netTotem = totalRentSaved - servicesTotal;

  const eur = (v) => (Number(v) || 0).toLocaleString('es-ES', { maximumFractionDigits: 0 });
  const pct = (v) => `${(Number(v) || 0).toFixed(1)}%`;
  // % sobre renta actual anual
  const bajadaPct = annualCurrent > 0 ? (annualSaving / annualCurrent) * 100 : 0;
  const costPctVsRent = annualCurrent > 0 ? (servicesAnnual / annualCurrent) * 100 : 0;
  const netPctVsRent = annualCurrent > 0 ? ((annualSaving - servicesAnnual) / annualCurrent) * 100 : 0;
  const roiTotal = servicesTotal > 0 ? (totalRentSaved / servicesTotal) * 100 : 0;

  return (
    <div className="bg-black/50 border border-white/15 rounded-xl overflow-hidden" data-testid="cost-benefit-summary">
      <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-white/10 bg-gradient-to-r from-white/5 to-transparent">
        <div className="flex items-center gap-2">
          <PieChart size={13} className="text-white/70"/>
          <span className="text-white text-xs font-black tracking-wide">RESUMEN COSTES · BENEFICIOS</span>
        </div>
        <span className="text-[10px] text-white/50">
          Horizonte <b className="text-white/80">{years.toFixed(1)} años</b>
          {neg.contract_end_date && <> · hasta {String(neg.contract_end_date).slice(0, 10)}</>}
        </span>
      </div>

      {/* Fila 1: Renta actual y Renta nueva */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-0 divide-x divide-white/10 border-b border-white/10">
        <div className="p-3">
          <div className="text-[9px] uppercase tracking-widest text-white/50 font-black">Renta actual</div>
          <div className="text-white text-lg font-black mt-1">{eur(annualCurrent)} €/año</div>
          <div className="text-[10px] text-white/40 mt-0.5">= {eur(annualCurrent / 12)} €/mes</div>
        </div>
        <div className="p-3">
          <div className="text-[9px] uppercase tracking-widest text-white/50 font-black">Renta propuesta</div>
          <div className="text-white text-lg font-black mt-1">{eur(annualFinal)} €/año</div>
          <div className="text-[10px] text-white/40 mt-0.5">
            Bajada <b className="text-[#A3FF12]">{eur(annualSaving)} €</b> · <b className="text-[#A3FF12]">{pct(bajadaPct)}</b>
          </div>
        </div>
      </div>

      {/* Fila 2: Costes y Beneficios (anual + total, con % sobre renta actual) */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-0 divide-x divide-white/10">
        {/* COLUMNA COSTES */}
        <div className="p-3 space-y-2">
          <div className="flex items-center gap-1.5 mb-1">
            <span className="w-1.5 h-1.5 rounded-full bg-orange-400"/>
            <span className="text-[10px] uppercase tracking-widest text-orange-300 font-black">Costes preventivo</span>
          </div>
          <SummaryRow label="Anual (in-kind)" value={`${eur(servicesAnnual)} €/año`} pctValue={pct(costPctVsRent)} pctLabel="s/renta" color="orange"/>
          <SummaryRow label={`Total (${years.toFixed(1)}a)`} value={`${eur(servicesTotal)} €`} color="orange" bold/>
          {items.length === 0 && (
            <div className="text-[10px] text-white/40 italic pt-1">
              Sin oferta de servicios definida. Diseña la oferta para calcular contrapartida.
            </div>
          )}
        </div>
        {/* COLUMNA BENEFICIOS */}
        <div className="p-3 space-y-2">
          <div className="flex items-center gap-1.5 mb-1">
            <span className="w-1.5 h-1.5 rounded-full bg-[#A3FF12]"/>
            <span className="text-[10px] uppercase tracking-widest text-[#A3FF12] font-black">Beneficios TOTEM</span>
          </div>
          <SummaryRow label="Ahorro renta (anual)" value={`${eur(annualSaving)} €/año`} pctValue={pct(bajadaPct)} pctLabel="s/renta" color="green"/>
          <SummaryRow label={`Ahorro renta (${years.toFixed(1)}a)`} value={`${eur(totalRentSaved)} €`} color="green" bold/>
        </div>
      </div>

      {/* Fila 3: Neto TOTEM — ahorro real anual del operador */}
      <div className="border-t border-white/10">
        <div className={`p-3 flex items-center justify-between gap-2 ${netTotem >= 0 ? 'bg-[#A3FF12]/10' : 'bg-red-500/10'}`}>
          <div className="min-w-0">
            <div className="text-[9px] uppercase tracking-widest text-white/60 font-black">Neto TOTEM · Ahorro real anual</div>
            <div className="text-[10px] text-white/50 mt-0.5">
              Ahorro renta − coste servicios · ROI <b className="text-white/80">{items.length ? pct(roiTotal) : '—'}</b>
            </div>
          </div>
          <div className={`font-black text-lg text-right flex-shrink-0 ${netTotem >= 0 ? 'text-[#A3FF12]' : 'text-red-400'}`}>
            <div>{netTotem >= 0 ? '+' : ''}{eur(netTotem)} €</div>
            <div className="text-[10px] font-bold">{pct(netPctVsRent)} anual s/renta</div>
          </div>
        </div>
      </div>
    </div>
  );
};

const SummaryRow = ({ label, value, pctValue, pctLabel, color, bold }) => {
  const colorClass = color === 'green' ? 'text-[#A3FF12]' : color === 'orange' ? 'text-orange-300' : 'text-white';
  return (
    <div className="flex items-center justify-between gap-2 text-xs">
      <span className="text-white/70">{label}</span>
      <span className="text-right flex items-baseline gap-1.5">
        {pctValue && <span className="text-[10px] text-white/40">{pctValue} <span className="text-white/30">{pctLabel}</span></span>}
        <span className={`${colorClass} ${bold ? 'font-black' : 'font-bold'}`}>{value}</span>
      </span>
    </div>
  );
};

const NegotiationDetail = ({ neg: initial, sites, onClose, onUpdated }) => {
  const [neg, setNeg] = useState(initial);
  const [strategies, setStrategies] = useState([]);
  const [selectedStrategies, setSelectedStrategies] = useState([]);
  const [selectedObligations, setSelectedObligations] = useState([]);
  const [includeServiceOffer, setIncludeServiceOffer] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [showServiceBuilder, setShowServiceBuilder] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showDossier, setShowDossier] = useState(false);
  const [showDuplicar, setShowDuplicar] = useState(false);

  useEffect(() => { setNeg(initial); }, [initial]);

  useEffect(() => {
    api.get(`/rent-reduction/${neg.id}/strategies`).then((r) => {
      const list = r.data || [];
      setStrategies(list);
      // por defecto todas seleccionadas (top-N)
      setSelectedStrategies(list.slice(0, 3).map((s) => s.id));
    }).catch(() => setStrategies([]));
  }, [neg.id]);

  // Sincroniza selección de obligaciones cuando llega el análisis normativo
  useEffect(() => {
    const obligations = neg.regulatory_analysis?.obligations || [];
    if (obligations.length && selectedObligations.length === 0) {
      // por defecto ninguna seleccionada — el usuario decide
      setSelectedObligations([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [neg.regulatory_analysis?.obligations?.length]);

  const toggleStrategy = (id) => {
    setSelectedStrategies((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };
  const toggleObligation = (id) => {
    setSelectedObligations((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const generateProposal = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post(`/rent-reduction/${neg.id}/ai-proposal`, {
        selected_strategies: selectedStrategies,
        selected_obligations: selectedObligations,
        include_service_offer: includeServiceOffer,
      });
      const refreshed = await api.get(`/rent-reduction/${neg.id}`).then((r) => r.data);
      setNeg(refreshed); onUpdated(refreshed);
      toast.success('Propuesta Adquiriam generada');
      setShowPreview(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error generando propuesta');
    } finally { setGenerating(false); }
  };

  const changeStatus = async (newStatus, extra = {}) => {
    setUpdating(true);
    try {
      const { data } = await api.patch(`/rent-reduction/${neg.id}`, { status: newStatus, ...extra });
      setNeg(data); onUpdated(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error actualizando');
    } finally { setUpdating(false); }
  };

  const sign = async () => {
    const final = window.prompt('Importe FINAL firmado (€/mes):', neg.target_rent_monthly);
    if (!final) return;
    await changeStatus('signed', { final_rent_monthly: Number(final) });
  };

  return (
    <div className="fixed inset-0 z-[10050] bg-black/85 backdrop-blur flex items-center justify-center p-4"
      onClick={onClose} data-testid="negotiation-detail-modal">
      <div onClick={(e) => e.stopPropagation()}
        className="bg-[#0a0a0a] border border-white/15 rounded-3xl w-full max-w-3xl max-h-[92vh] overflow-y-auto p-5 space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-white font-black text-lg">{neg.site_id}</div>
            <div className="mt-1"><StatusPill status={neg.status}/></div>
          </div>
          <button onClick={onClose} className="text-white/40 hover:text-white p-1"><XCircle size={22}/></button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-center">
          <MiniStat label="Renta actual (anual)"  value={`${((neg.current_rent_monthly  || 0) * 12).toLocaleString('es-ES', {maximumFractionDigits:0})} €`}/>
          <MiniStat label="Objetivo (anual)"      value={`${((neg.target_rent_monthly   || 0) * 12).toLocaleString('es-ES', {maximumFractionDigits:0})} €`}/>
          {neg.offered_rent_monthly && <MiniStat label="Oferta (anual)"  value={`${((neg.offered_rent_monthly || 0) * 12).toLocaleString('es-ES', {maximumFractionDigits:0})} €`}/>}
          {neg.final_rent_monthly   && <MiniStat label="Firmada (anual)" value={`${((neg.final_rent_monthly   || 0) * 12).toLocaleString('es-ES', {maximumFractionDigits:0})} €`} accent/>}
        </div>

        {/* Panel de ahorro anual — usa la firmada si existe, si no la objetivo */}
        <TargetPctEditor neg={neg} onUpdated={onUpdated}/>
        {(() => {
          const cur = Number(neg.current_rent_monthly) || 0;
          const fin = Number(neg.final_rent_monthly ?? neg.target_rent_monthly) || 0;
          if (!cur) return null;
          const monthlySaving = Math.max(0, cur - fin);
          const annualSaving = monthlySaving * 12;
          const savingPct = cur > 0 ? (monthlySaving / cur) * 100 : 0;
          const annualCurrent = cur * 12;
          const annualFinal = fin * 12;
          const scenario = neg.final_rent_monthly ? 'firmada' : 'objetivo';
          return (
            <div className="bg-gradient-to-br from-[#A3FF12]/10 to-emerald-500/5 border border-[#A3FF12]/30 rounded-xl p-3" data-testid="annual-saving-panel">
              <div className="flex items-center gap-2 mb-2">
                <TrendingDown size={14} className="text-[#A3FF12]"/>
                <span className="text-[#A3FF12] text-xs font-black tracking-wide">
                  AHORRO ANUAL ESTIMADO ({scenario === 'firmada' ? 'FIRMADA' : 'SEGÚN OBJETIVO'})
                </span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-center">
                <MiniStat label="Renta anual actual" value={`${annualCurrent.toLocaleString('es-ES', {maximumFractionDigits:0})} €`}/>
                <MiniStat label="Renta anual nueva" value={`${annualFinal.toLocaleString('es-ES', {maximumFractionDigits:0})} €`}/>
                <MiniStat label="% ahorro" value={`${savingPct.toLocaleString('es-ES', {minimumFractionDigits:1, maximumFractionDigits:1})}%`} accent/>
                <MiniStat label="Ahorro anual" value={`${annualSaving.toLocaleString('es-ES', {maximumFractionDigits:0})} €`} accent/>
              </div>
              <div className="text-[11px] text-white/60 mt-2 text-center">
                Ahorro mensual: <b className="text-white">{monthlySaving.toLocaleString('es-ES', {maximumFractionDigits:2})} €/mes</b>
                {' · '}Equivale a <b className="text-white">{(annualSaving / 30).toLocaleString('es-ES', {maximumFractionDigits:0})} €/día</b>
              </div>
            </div>
          );
        })()}

        {/* Business logic + Resumen costes/beneficios */}
        <BusinessLogicPanel neg={neg} sites={sites}/>
        <CostBenefitSummary neg={neg}/>

        {/* Estrategias normativas aplicables */}
        {strategies.length > 0 && (
          <div className="bg-[#A3FF12]/5 border border-[#A3FF12]/20 rounded-xl p-3">
            <div className="flex items-center justify-between gap-2 mb-2">
              <div className="flex items-center gap-2">
                <ScrollText size={14} className="text-[#A3FF12]"/>
                <span className="text-[#A3FF12] text-xs font-black tracking-wide">PALANCAS NORMATIVAS APLICABLES</span>
              </div>
              <span className="text-[10px] text-white/50">
                {selectedStrategies.length} de {strategies.length} incluidas en propuesta
              </span>
            </div>
            <div className="space-y-2">
              {strategies.map((s) => {
                const checked = selectedStrategies.includes(s.id);
                return (
                  <div key={s.id} className={`border rounded-lg overflow-hidden transition-colors ${checked ? 'border-[#A3FF12]/40 bg-[#A3FF12]/5' : 'border-white/10 bg-black/20'}`}>
                    <label className="flex items-start gap-2 p-2 cursor-pointer">
                      <input type="checkbox" checked={checked} onChange={() => toggleStrategy(s.id)}
                        data-testid={`chk-strategy-${s.id}`}
                        className="w-4 h-4 accent-[#A3FF12] flex-shrink-0 mt-0.5"/>
                      <details className="text-xs flex-1">
                        <summary className="cursor-pointer text-white font-bold hover:text-[#A3FF12]">
                          {s.title} <span className="text-white/40 font-normal">· ROI {s.typical_roi_pct} · {s.duration_years} años</span>
                        </summary>
                        <div className="mt-1 text-white/70">
                          <p className="mb-1">{s.summary}</p>
                          <ul className="list-disc list-inside space-y-0.5 text-white/60">
                            {s.arguments.slice(0, 3).map((a, i) => <li key={i} dangerouslySetInnerHTML={{ __html: a }}/>)}
                          </ul>
                          <div className="text-white/40 mt-1 text-[10px]"><b>Normativa:</b> {s.regulation_refs.join(' · ')}</div>
                        </div>
                      </details>
                    </label>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Análisis normativo Adquiriam — específico al inmueble */}
        <RegulatoryAnalysisSection
          neg={neg}
          onUpdated={(n) => { setNeg(n); onUpdated(n); }}
          selectedObligations={selectedObligations}
          onToggleObligation={toggleObligation}
        />

        {/* Servicios preventivos como palanca in-kind */}
        <ServiceOfferSection
          neg={neg}
          onOpenBuilder={() => setShowServiceBuilder(true)}
          onUpdated={(n) => { setNeg(n); onUpdated(n); }}
        />

        {/* Propuesta Adquiriam */}
        <div className="bg-white/5 border border-white/10 rounded-xl p-3">
          <div className="flex items-center justify-between gap-2 mb-2">
            <div className="flex items-center gap-2">
              <Sparkles size={14} className="text-purple-400"/>
              <span className="text-white text-xs font-black">Propuesta Adquiriam</span>
            </div>
            <button onClick={generateProposal} disabled={generating}
              data-testid="generate-ai-proposal"
              className="bg-purple-500 text-white font-black text-[11px] px-3 py-1.5 rounded-lg hover:bg-purple-400 disabled:opacity-40 flex items-center gap-1">
              {generating ? <Loader2 className="animate-spin" size={11}/> : <Sparkles size={11}/>}
              {generating ? 'Generando…' : (neg.ai_proposal ? 'Regenerar' : 'Generar con Adquiriam')}
            </button>
          </div>
          {/* Resumen de qué se incluye */}
          <div className="bg-black/30 border border-white/5 rounded-lg p-2 mb-2 text-[11px]">
            <div className="text-white/50 uppercase text-[9px] font-bold tracking-wide mb-1">Se incluirán en la propuesta</div>
            <div className="flex flex-wrap items-center gap-2 text-white/70">
              <span className={`px-2 py-0.5 rounded-full border ${selectedStrategies.length > 0 ? 'bg-[#A3FF12]/10 border-[#A3FF12]/40 text-[#A3FF12]' : 'bg-white/5 border-white/10 text-white/40'}`}>
                {selectedStrategies.length} palanca{selectedStrategies.length === 1 ? '' : 's'} normativa{selectedStrategies.length === 1 ? '' : 's'}
              </span>
              <span className={`px-2 py-0.5 rounded-full border ${selectedObligations.length > 0 ? 'bg-blue-500/10 border-blue-500/40 text-blue-300' : 'bg-white/5 border-white/10 text-white/40'}`}>
                {selectedObligations.length} obligaci{selectedObligations.length === 1 ? 'ón' : 'ones'} Adquiriam
              </span>
              <label className={`px-2 py-0.5 rounded-full border cursor-pointer flex items-center gap-1 ${includeServiceOffer && neg.service_offer?.items?.length ? 'bg-orange-500/10 border-orange-500/40 text-orange-300' : 'bg-white/5 border-white/10 text-white/40'}`}
                title={neg.service_offer?.items?.length ? 'Incluir oferta de servicios preventivos' : 'No hay oferta de servicios definida'}>
                <input type="checkbox" checked={includeServiceOffer} onChange={(e) => setIncludeServiceOffer(e.target.checked)}
                  disabled={!neg.service_offer?.items?.length}
                  data-testid="chk-include-service-offer"
                  className="w-3 h-3 accent-orange-400"/>
                Servicios preventivos ({neg.service_offer?.items?.length || 0})
              </label>
            </div>
          </div>
          {neg.ai_proposal ? (
            <div className="space-y-2 text-xs">
              <div className="flex items-center justify-between gap-2">
                <div><span className="text-white/50">Asunto:</span> <span className="text-white font-bold">{neg.ai_proposal.subject}</span></div>
                <button onClick={() => setShowPreview(true)}
                  data-testid="btn-open-preview"
                  className="bg-white/10 hover:bg-white/20 text-white font-black text-[11px] px-3 py-1.5 rounded-lg flex items-center gap-1 whitespace-nowrap">
                  <Eye size={11}/> Vista previa
                </button>
              </div>
              <div className="bg-black/40 rounded p-3 text-white/80 max-h-64 overflow-y-auto"
                   dangerouslySetInnerHTML={{ __html: neg.ai_proposal.body }}/>
              {neg.ai_proposal.arguments?.length > 0 && (
                <div className="pt-2 border-t border-white/10">
                  <div className="text-white/50 text-[10px] uppercase mb-1">Argumentos clave</div>
                  <ul className="space-y-0.5">
                    {neg.ai_proposal.arguments.map((a, i) => <li key={i} className="text-white/70">· {a}</li>)}
                  </ul>
                </div>
              )}
            </div>
          ) : (
            <div className="text-white/40 text-xs italic">Aún no se ha generado propuesta. Al pulsar "Generar" Gemini escribirá una carta usando las palancas normativas aplicables + histórico del site.</div>
          )}
        </div>

        {/* Acciones de flujo */}
        <div className="flex flex-wrap gap-2">
          <button onClick={() => setShowDossier(true)}
            data-testid="btn-open-dossier"
            className="bg-white text-black font-black text-xs px-3 py-1.5 rounded-lg hover:bg-white/90 flex items-center gap-1 border border-white/20">
            <FileDown size={12}/> Generar Dosier
          </button>
          <button onClick={() => setShowDuplicar(true)}
            data-testid="btn-open-duplicar"
            className="bg-blue-500/20 border border-blue-400/40 text-blue-200 font-black text-xs px-3 py-1.5 rounded-lg hover:bg-blue-500/30 flex items-center gap-1">
            <Copy size={12}/> Duplicar a otros sites
          </button>
          {neg.status === 'identified' && <BtnAction onClick={() => changeStatus('visit_scheduled')} loading={updating}>Programar visita</BtnAction>}
          {neg.status === 'visit_scheduled' && <BtnAction onClick={() => changeStatus('visit_done')} loading={updating}>Marcar visita hecha</BtnAction>}
          {neg.status === 'visit_done' && <BtnAction onClick={() => changeStatus('proposal_sent')} loading={updating}>Marcar propuesta enviada</BtnAction>}
          {neg.status === 'proposal_sent' && <BtnAction onClick={() => changeStatus('in_negotiation')} loading={updating}>En negociación</BtnAction>}
          {['in_negotiation', 'proposal_sent'].includes(neg.status) && (
            <BtnAction onClick={sign} loading={updating} kind="success">Firmar (importe final)</BtnAction>
          )}
          {['in_negotiation', 'proposal_sent'].includes(neg.status) && (
            <BtnAction onClick={() => changeStatus('rejected')} loading={updating} kind="danger">Rechazada</BtnAction>
          )}
        </div>

        {/* Audit log */}
        <details className="bg-black/40 border border-white/10 rounded-xl">
          <summary className="cursor-pointer text-white/70 text-xs font-bold p-3 hover:text-white">
            Trazabilidad ({neg.history?.length || 0} eventos)
          </summary>
          <div className="p-3 pt-0 space-y-1 max-h-40 overflow-y-auto">
            {(neg.history || []).map((h, i) => (
              <div key={i} className="text-[11px] text-white/60 font-mono">
                <span className="text-white/40">{h.ts?.slice(0, 16).replace('T', ' ')}</span>
                {' · '}
                <span className="text-[#A3FF12]">{h.actor}</span>
                {' · '}
                <span className="text-white">{h.action}</span>
                {h.note && <span className="text-white/50"> — {h.note}</span>}
              </div>
            ))}
          </div>
        </details>
      </div>

      {showServiceBuilder && (
        <ServiceOfferBuilder
          neg={neg}
          onClose={() => setShowServiceBuilder(false)}
          onSaved={(n) => { setNeg(n); onUpdated(n); setShowServiceBuilder(false); }}
        />
      )}

      {showPreview && neg.ai_proposal && (
        <ProposalPreviewModal
          neg={neg}
          onClose={() => setShowPreview(false)}
          onMarkSent={async () => {
            await changeStatus('proposal_sent');
            setShowPreview(false);
            toast.success('Propuesta marcada como enviada');
          }}
        />
      )}

      {showDossier && (
        <BajadaRentaDossier
          neg={neg}
          onClose={() => setShowDossier(false)}
        />
      )}

      {showDuplicar && (
        <DuplicarNegociacionModal
          neg={neg}
          onClose={() => setShowDuplicar(false)}
          onCloned={() => { onUpdated && onUpdated(neg); }}
        />
      )}
    </div>
  );
};

// ============ Vista previa de la carta generada ============
const ProposalPreviewModal = ({ neg, onClose, onMarkSent }) => {
  const p = neg.ai_proposal || {};
  const currentAnnual = Math.round((neg.current_rent_monthly || 0) * 12);
  const targetAnnual = Math.round((neg.target_rent_monthly || 0) * 12);
  const savingAnnual = currentAnnual - targetAnnual;
  const reductionPct = currentAnnual > 0 ? Math.round((savingAnnual / currentAnnual) * 1000) / 10 : 0;
  const today = new Date().toLocaleDateString('es-ES', { day: '2-digit', month: 'long', year: 'numeric' });

  const copyHtml = async () => {
    try {
      const html = `<h3>${p.subject || ''}</h3>${p.body || ''}`;
      if (navigator.clipboard && window.ClipboardItem) {
        await navigator.clipboard.write([
          new ClipboardItem({
            'text/html': new Blob([html], { type: 'text/html' }),
            'text/plain': new Blob([(p.body || '').replace(/<[^>]+>/g, '')], { type: 'text/plain' }),
          }),
        ]);
      } else {
        await navigator.clipboard.writeText(html);
      }
      toast.success('Carta copiada al portapapeles');
    } catch { toast.error('No se pudo copiar'); }
  };

  const copyText = async () => {
    try {
      const text = `Asunto: ${p.subject || ''}\n\n${(p.body || '').replace(/<br\s*\/?>/gi, '\n').replace(/<\/p>/gi, '\n\n').replace(/<[^>]+>/g, '').replace(/\n{3,}/g, '\n\n').trim()}`;
      await navigator.clipboard.writeText(text);
      toast.success('Texto copiado al portapapeles');
    } catch { toast.error('No se pudo copiar'); }
  };

  const printLetter = () => {
    const w = window.open('', '_blank', 'width=800,height=900');
    if (!w) { toast.error('El navegador bloqueó la ventana emergente'); return; }
    w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>${p.subject || 'Propuesta'}</title>
      <style>
        body { font-family: Georgia, 'Times New Roman', serif; color:#111; max-width:720px; margin:40px auto; padding:0 32px; line-height:1.55; }
        h1 { font-size:15px; text-transform:uppercase; letter-spacing:1px; color:#555; margin:0 0 4px; }
        h2 { font-size:20px; margin:0 0 24px; color:#111; }
        .meta { color:#666; font-size:12px; margin-bottom:32px; }
        .body p { margin:0 0 12px; }
        @media print { body { margin:0; padding:24px; } }
      </style></head><body>
        <h1>Propuesta de renegociación</h1>
        <h2>${p.subject || ''}</h2>
        <div class="meta">Site: <b>${neg.site_id || ''}</b>${neg.site_name ? ' — ' + neg.site_name : ''}<br/>Fecha: ${today}</div>
        <div class="body">${p.body || ''}</div>
      </body></html>`);
    w.document.close();
    setTimeout(() => w.print(), 250);
  };

  return (
    <div className="fixed inset-0 z-[10060] bg-black/90 backdrop-blur flex items-center justify-center p-4"
      onClick={onClose} data-testid="proposal-preview-modal">
      <div onClick={(e) => e.stopPropagation()}
        className="w-full max-w-4xl max-h-[94vh] flex flex-col bg-[#0a0a0a] border border-white/15 rounded-3xl overflow-hidden">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-white/10 bg-gradient-to-r from-purple-500/10 to-transparent">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <Sparkles size={14} className="text-purple-400"/>
              <span className="text-purple-300 text-[10px] font-black uppercase tracking-widest">Vista previa · Adquiriam</span>
            </div>
            <div className="text-white font-black text-base truncate">{p.subject || 'Propuesta sin asunto'}</div>
            <div className="text-white/50 text-[11px] mt-0.5">
              Site <b className="text-white/80">{neg.site_id}</b>
              {neg.site_name && <> · {neg.site_name}</>}
              {' · '}
              <span className="text-white/40">{today}</span>
            </div>
          </div>
          <button onClick={onClose} data-testid="btn-close-preview"
            className="text-white/40 hover:text-white p-1 flex-shrink-0"><XCircle size={22}/></button>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-3 gap-2 px-5 py-3 bg-black/40 border-b border-white/5 text-center">
          <div>
            <div className="text-white/40 text-[9px] uppercase tracking-wider">Renta actual (anual)</div>
            <div className="text-white font-black text-sm mt-0.5">{currentAnnual.toLocaleString('es-ES')} €</div>
          </div>
          <div>
            <div className="text-white/40 text-[9px] uppercase tracking-wider">Propuesta (anual)</div>
            <div className="text-white font-black text-sm mt-0.5">{targetAnnual.toLocaleString('es-ES')} €</div>
          </div>
          <div>
            <div className="text-[#A3FF12]/70 text-[9px] uppercase tracking-wider">Ahorro anual</div>
            <div className="text-[#A3FF12] font-black text-sm mt-0.5">
              {savingAnnual.toLocaleString('es-ES')} € <span className="text-[10px] font-bold">({reductionPct}%)</span>
            </div>
          </div>
        </div>

        {/* Letter body — paper look */}
        <div className="flex-1 overflow-y-auto p-6 bg-neutral-100">
          <div className="mx-auto max-w-2xl bg-white shadow-2xl rounded-sm px-10 py-12"
               style={{ fontFamily: 'Georgia, "Times New Roman", serif', color: '#1a1a1a', lineHeight: 1.65 }}
               data-testid="proposal-preview-body">
            <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 mb-1">Propuesta de renegociación de arrendamiento</div>
            <div className="text-[18px] font-bold mb-6 text-neutral-900">{p.subject || ''}</div>
            <div className="text-[11px] text-neutral-500 mb-8 pb-4 border-b border-neutral-200">
              Emplazamiento: <b className="text-neutral-800">{neg.site_id}</b>
              {neg.site_name && <> — {neg.site_name}</>}
              <br/>Fecha: {today}
            </div>
            <div className="text-[14px] proposal-letter"
                 dangerouslySetInnerHTML={{ __html: p.body || '' }}/>
            {p.arguments?.length > 0 && (
              <div className="mt-8 pt-4 border-t border-neutral-200">
                <div className="text-[10px] uppercase tracking-wider text-neutral-500 font-bold mb-2">Argumentos clave</div>
                <ul className="text-[12px] text-neutral-700 space-y-1">
                  {p.arguments.map((a, i) => <li key={i}>· {a}</li>)}
                </ul>
              </div>
            )}
          </div>
          <style>{`.proposal-letter p { margin: 0 0 12px; } .proposal-letter b, .proposal-letter strong { color: #000; }`}</style>
        </div>

        {/* Actions */}
        <div className="flex flex-wrap items-center justify-between gap-2 px-5 py-3 border-t border-white/10 bg-black/50">
          <div className="flex items-center gap-2">
            <button onClick={copyHtml} data-testid="btn-copy-html"
              className="bg-white/10 hover:bg-white/20 text-white text-[11px] font-black px-3 py-2 rounded-lg flex items-center gap-1">
              <Copy size={12}/> Copiar
            </button>
            <button onClick={copyText} data-testid="btn-copy-text"
              className="bg-white/10 hover:bg-white/20 text-white text-[11px] font-black px-3 py-2 rounded-lg flex items-center gap-1">
              <Copy size={12}/> Copiar texto
            </button>
            <button onClick={printLetter} data-testid="btn-print-letter"
              className="bg-white/10 hover:bg-white/20 text-white text-[11px] font-black px-3 py-2 rounded-lg flex items-center gap-1">
              <Printer size={12}/> Imprimir / PDF
            </button>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onClose} data-testid="btn-preview-close-bottom"
              className="text-white/60 hover:text-white text-[11px] font-black px-3 py-2 rounded-lg">
              Cerrar
            </button>
            {['identified', 'visit_scheduled', 'visit_done'].includes(neg.status) && (
              <button onClick={onMarkSent} data-testid="btn-mark-sent"
                className="bg-[#A3FF12] hover:bg-[#b8ff3a] text-black text-[11px] font-black px-4 py-2 rounded-lg flex items-center gap-1">
                <Send size={12}/> Marcar como enviada
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ============ Análisis normativo IA (obligaciones del inmueble) ============
const URGENCY_META = {
  alta:  { color: '#ef4444', label: 'ALTA' },
  media: { color: '#fbbf24', label: 'MEDIA' },
  baja:  { color: '#94a3b8', label: 'BAJA' },
};

const CATEGORY_LABEL = {
  ITE: 'ITE / IEE',
  accesibilidad: 'Accesibilidad',
  ascensor: 'Ascensor',
  energetica: 'Rehab. energética',
  amianto: 'Amianto',
  incendios: 'Incendios',
  electrica: 'Eléctrica / OCA',
  renovables: 'Renovables',
  urbanistica: 'Urbanismo',
  arrendamiento: 'Arrendamiento',
  otro: 'Otro',
};

const RegulatoryAnalysisSection = ({ neg, onUpdated, selectedObligations = [], onToggleObligation = () => {} }) => {
  const [analyzing, setAnalyzing] = useState(false);
  const report = neg.regulatory_analysis;

  const analyze = async () => {
    setAnalyzing(true);
    try {
      await api.post(`/rent-reduction/${neg.id}/regulatory-analysis`);
      const refreshed = await api.get(`/rent-reduction/${neg.id}`).then((r) => r.data);
      onUpdated(refreshed);
      toast.success(`Análisis normativo generado (${refreshed.regulatory_analysis?.obligations?.length || 0} obligaciones)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error analizando normativa');
    } finally {
      setAnalyzing(false);
    }
  };

  return (
    <div className="bg-blue-500/5 border border-blue-500/25 rounded-xl p-3" data-testid="regulatory-analysis-section">
      <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
        <div className="flex items-center gap-2">
          <Gavel size={14} className="text-blue-400"/>
          <span className="text-blue-300 text-xs font-black tracking-wide">
            ANÁLISIS NORMATIVO DEL INMUEBLE {report ? `· ${report.obligations?.length || 0} obligaciones` : ''}
          </span>
        </div>
        <button onClick={analyze} disabled={analyzing}
          data-testid="btn-analyze-regulations"
          className="bg-blue-500 text-white font-black text-[11px] px-3 py-1.5 rounded-lg hover:bg-blue-400 disabled:opacity-40 flex items-center gap-1">
          {analyzing ? <Loader2 className="animate-spin" size={11}/> : <Sparkles size={11}/>}
          {analyzing ? 'Analizando…' : (report ? 'Re-analizar' : 'Analizar con Adquiriam')}
        </button>
      </div>

      {!report ? (
        <div className="text-white/40 text-xs italic">
          Detecta con Adquiriam qué obligaciones normativas (ITE, accesibilidad, ascensor, amianto, rehab. energética, autoconsumo, etc.) debe cumplir el propietario, y cómo podemos financiar cada una a cambio de bajada de renta.
        </div>
      ) : (
        <div className="space-y-3">
          {report.summary && (
            <div className="text-white/80 text-xs bg-black/30 rounded-lg p-2 border-l-2 border-blue-400 italic">
              {report.summary}
            </div>
          )}
          {report.assumptions?.length > 0 && (
            <details className="text-[11px]">
              <summary className="cursor-pointer text-white/50 hover:text-white/80">
                <AlertTriangle size={10} className="inline mr-1 text-yellow-500"/>
                Asunciones del análisis ({report.assumptions.length})
              </summary>
              <ul className="mt-1 pl-4 text-white/60 space-y-0.5">
                {report.assumptions.map((a, i) => <li key={i}>· {a}</li>)}
              </ul>
            </details>
          )}
          <div className="space-y-2">
            {(report.obligations || []).map((ob, i) => {
              const urg = URGENCY_META[ob.urgency] || URGENCY_META.media;
              const obId = ob.id || String(i);
              const checked = selectedObligations.includes(obId);
              return (
                <div key={obId}
                  className={`border rounded-lg overflow-hidden transition-colors ${checked ? 'border-[#A3FF12]/40 bg-[#A3FF12]/5' : 'border-white/10 bg-black/30'}`}
                  data-testid={`regulation-${obId}`}>
                  <div className="flex items-center gap-2 p-2 hover:bg-white/5">
                    <input type="checkbox" checked={checked} onChange={() => onToggleObligation(obId)}
                      data-testid={`chk-obligation-${obId}`}
                      className="w-4 h-4 accent-[#A3FF12] flex-shrink-0"
                      title="Incluir en la propuesta Adquiriam"/>
                    <details className="flex-1">
                      <summary className="cursor-pointer flex items-center gap-2">
                        <span className="text-[9px] font-black px-1.5 py-0.5 rounded flex-shrink-0"
                          style={{ backgroundColor: `${urg.color}20`, color: urg.color, border: `1px solid ${urg.color}50` }}>
                          {urg.label}
                        </span>
                        <span className="text-[9px] font-bold text-blue-300 bg-blue-500/10 px-1.5 py-0.5 rounded flex-shrink-0">
                          {CATEGORY_LABEL[ob.category] || ob.category?.toUpperCase()}
                        </span>
                        <span className="text-white text-xs font-bold truncate flex-1">{ob.norma}</span>
                        {ob.estimated_cost_eur && (
                          <span className="text-[10px] text-white/50 font-mono flex-shrink-0">{ob.estimated_cost_eur}</span>
                        )}
                      </summary>
                      <div className="p-3 pt-2 space-y-2 text-xs border-t border-white/5 mt-2">
                        <div>
                          <span className="text-white/50 text-[10px] uppercase font-bold">Obligación</span>
                          <p className="text-white/90 mt-0.5">{ob.obligation}</p>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-[11px]">
                          <div>
                        <span className="text-white/50 text-[10px] uppercase font-bold">Plazo</span>
                        <p className="text-white/80 mt-0.5">{ob.deadline || '—'}</p>
                      </div>
                      <div>
                        <span className="text-white/50 text-[10px] uppercase font-bold">Coste estimado</span>
                        <p className="text-white/80 mt-0.5">{ob.estimated_cost_eur || '—'}</p>
                      </div>
                    </div>
                    <div className="bg-[#A3FF12]/5 border border-[#A3FF12]/25 rounded-lg p-2">
                      <span className="text-[#A3FF12] text-[10px] uppercase font-bold tracking-wide">Palanca para nosotros</span>
                      <p className="text-white/90 mt-1">{ob.our_leverage}</p>
                    </div>
                    <div className="text-[10px] text-white/40 flex items-center justify-between">
                      <span>Confianza Adquiriam: <b className="text-white/70">{ob.confidence || 'media'}</b></span>
                      <span>#{obId}</span>
                    </div>
                      </div>
                    </details>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="text-[10px] text-white/40 text-right">
            Generado {report.generated_at?.slice(0, 16).replace('T', ' ')} · {report.model}
          </div>
        </div>
      )}
    </div>
  );
};


// ============ Sección de servicios preventivos (in-kind) ============
const ServiceOfferSection = ({ neg, onOpenBuilder, onUpdated }) => {
  const [creatingTasks, setCreatingTasks] = useState(false);
  const offer = neg.service_offer;

  const createTasks = async () => {
    if (!window.confirm('Se crearán tareas preventivas reales en el módulo Preventivo, vinculadas a esta negociación. ¿Continuar?')) return;
    setCreatingTasks(true);
    try {
      const { data } = await api.post(`/rent-reduction/${neg.id}/create-preventive-tasks`);
      toast.success(`${data.created} tareas preventivas creadas y vinculadas al site.`);
      const refreshed = await api.get(`/rent-reduction/${neg.id}`).then((r) => r.data);
      onUpdated(refreshed);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error creando tareas preventivas');
    } finally {
      setCreatingTasks(false);
    }
  };

  return (
    <div className="bg-orange-500/5 border border-orange-500/20 rounded-xl p-3" data-testid="service-offer-section">
      <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
        <div className="flex items-center gap-2">
          <Hammer size={14} className="text-orange-400"/>
          <span className="text-orange-300 text-xs font-black tracking-wide">SERVICIOS PREVENTIVOS COMO PALANCA</span>
        </div>
        <button onClick={onOpenBuilder} data-testid="btn-open-service-builder"
          className="bg-orange-500 text-black font-black text-[11px] px-3 py-1.5 rounded-lg hover:bg-orange-400 flex items-center gap-1">
          {offer?.items?.length ? <><Wrench size={11}/>Editar oferta</> : <><Plus size={11}/>Diseñar oferta</>}
        </button>
      </div>
      {offer?.items?.length ? (
        <div className="text-xs space-y-2">
          {(() => {
            let yearsToEnd = Number(offer.duration_years) || 1;
            if (neg.contract_end_date) {
              const end = new Date(neg.contract_end_date);
              if (!isNaN(end.getTime())) {
                yearsToEnd = Math.max(0, (end.getTime() - Date.now()) / (365.25 * 24 * 60 * 60 * 1000));
              }
            }
            const totalUntilEnd = (offer.items || []).reduce((sum, it) => {
              const val = Number(it.market_value_eur) || 0;
              const freq = (it.frequency_months || 12) / 12;
              const execs = Math.floor(yearsToEnd / freq) + (yearsToEnd % freq >= freq / 2 ? 1 : 0);
              return sum + val * execs;
            }, 0);
            // Ahorro/pago para TOTEM
            const curMonthly = Number(neg.current_rent_monthly) || 0;
            const targetMonthly = Number(neg.target_rent_monthly) || 0;
            const annualReduction = (curMonthly - targetMonthly) * 12;
            const ahorroTotem = Math.max(0, annualReduction * yearsToEnd);
            const netoTotem = ahorroTotem - totalUntilEnd;
            return (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  <MiniStat label="Servicios" value={offer.items.length}/>
                  <MiniStat label="Valor anual eq." value={`${(offer.annual_total_eur || 0).toLocaleString('es-ES')} €`}/>
                  <MiniStat label="Equiv. bajada" value={`${((offer.equivalent_reduction_monthly_eur || 0) * 12).toLocaleString('es-ES', {maximumFractionDigits:0})} €/año`} accent/>
                  <MiniStat label={neg.contract_end_date ? `Hasta fin contrato (${yearsToEnd.toFixed(1)}a)` : `Total ${offer.duration_years || 1}a`}
                            value={`${totalUntilEnd.toLocaleString('es-ES', {maximumFractionDigits:0})} €`} accent/>
                </div>
                {/* Balance TOTEM: ahorro vs importe a abonar */}
                <div className="grid grid-cols-3 gap-2 mt-2">
                  <div className="bg-[#A3FF12]/10 border border-[#A3FF12]/30 rounded-lg py-1.5 px-2 text-center">
                    <div className="text-[9px] uppercase tracking-widest text-white/50">Ahorro TOTEM</div>
                    <div className="text-[#A3FF12] font-black text-sm">{ahorroTotem.toLocaleString('es-ES',{maximumFractionDigits:0})} €</div>
                  </div>
                  <div className="bg-orange-500/10 border border-orange-400/40 rounded-lg py-1.5 px-2 text-center">
                    <div className="text-[9px] uppercase tracking-widest text-white/50">Importe a abonar</div>
                    <div className="text-orange-300 font-black text-sm">{totalUntilEnd.toLocaleString('es-ES',{maximumFractionDigits:0})} €</div>
                  </div>
                  <div className={`rounded-lg py-1.5 px-2 text-center border ${netoTotem >= 0 ? 'bg-[#A3FF12]/5 border-[#A3FF12]/40' : 'bg-red-500/10 border-red-400/40'}`}>
                    <div className="text-[9px] uppercase tracking-widest text-white/50">Neto TOTEM</div>
                    <div className={`font-black text-sm ${netoTotem >= 0 ? 'text-[#A3FF12]' : 'text-red-400'}`}>
                      {netoTotem >= 0 ? '+' : ''}{netoTotem.toLocaleString('es-ES',{maximumFractionDigits:0})} €
                    </div>
                  </div>
                </div>
              </>
            );
          })()}
          <ul className="space-y-0.5 text-white/70">
            {offer.items.slice(0, 5).map((it, i) => {
              const freq = Number(it.frequency_months) || 12;
              const unit = Number(it.market_value_eur) || 0;
              const isMultiYear = freq > 12;
              // Cálculos para servicios plurianuales (cada > 1 año)
              let yrs = Number(offer.duration_years) || 1;
              if (neg.contract_end_date) {
                const end = new Date(neg.contract_end_date);
                if (!isNaN(end.getTime())) {
                  yrs = Math.max(0, (end.getTime() - Date.now()) / (365.25 * 24 * 60 * 60 * 1000));
                }
              }
              const freqYears = freq / 12;
              const execs = Math.floor(yrs / freqYears) + (yrs % freqYears >= freqYears / 2 ? 1 : 0);
              const totalPeriod = unit * execs;
              const annualEq = unit / freqYears;
              const freqLbl = freq >= 12 && freq % 12 === 0 ? `${freq / 12}a` : `${freq}m`;
              return (
                <li key={i} className="flex items-center justify-between border-b border-white/5 py-1 gap-2">
                  <span className="truncate">· {it.title}</span>
                  <span className="text-white/40 text-[10px] flex-shrink-0 text-right">
                    {unit.toLocaleString('es-ES', {maximumFractionDigits:0})} € · cada {freqLbl}
                    {isMultiYear && (
                      <span className="ml-2 text-white/60">
                        · Total {totalPeriod.toLocaleString('es-ES', {maximumFractionDigits:0})} €
                        <span className="text-white/40"> · {annualEq.toLocaleString('es-ES', {maximumFractionDigits:0})} €/año</span>
                      </span>
                    )}
                  </span>
                </li>
              );
            })}
            {offer.items.length > 5 && <li className="text-white/40 italic text-[10px]">+{offer.items.length - 5} más...</li>}
          </ul>
          {!neg.linked_preventive_task_ids?.length && (
            <button onClick={createTasks} disabled={creatingTasks}
              data-testid="btn-create-preventive-tasks"
              className="w-full mt-2 bg-white/10 hover:bg-white/20 text-white text-[11px] font-bold px-3 py-2 rounded-lg flex items-center justify-center gap-2 disabled:opacity-40">
              {creatingTasks ? <Loader2 className="animate-spin" size={12}/> : <Plus size={12}/>}
              {creatingTasks ? 'Creando…' : 'Crear tareas preventivas reales en Mantenimiento'}
            </button>
          )}
          {neg.linked_preventive_task_ids?.length > 0 && (
            <div className="text-[10px] text-[#A3FF12] flex items-center gap-1 pt-1">
              <CheckCircle size={11}/> {neg.linked_preventive_task_ids.length} tareas preventivas ya creadas y vinculadas
            </div>
          )}
        </div>
      ) : (
        <div className="text-white/40 text-xs italic">
          Ofrece al propietario ejecutar servicios de mantenimiento (limpieza, revisión de impermeabilización, desbroce, etc) sin coste, y compensa su valor de mercado como bajada de renta.
        </div>
      )}
    </div>
  );
};

const ServiceOfferBuilder = ({ neg, onClose, onSaved }) => {
  const [catalog, setCatalog] = useState([]);
  const [selected, setSelected] = useState(() => {
    const map = {};
    (neg.service_offer?.items || []).forEach((it) => { map[it.id] = it; });
    return map;
  });

  // Duración del acuerdo: por defecto se calcula automáticamente hasta fin de
  // contrato. Si el usuario "desbloquea" el input, se guarda un override.
  const autoYears = useMemo(() => {
    if (neg.contract_end_date) {
      const end = new Date(neg.contract_end_date);
      if (!isNaN(end.getTime())) {
        return Math.max(0, (end.getTime() - Date.now()) / (365.25 * 24 * 60 * 60 * 1000));
      }
    }
    return neg.service_offer?.duration_years || 1;
  }, [neg.contract_end_date, neg.service_offer]);
  const [durationOverride, setDurationOverride] = useState(null);
  const duration = durationOverride ?? autoYears;
  // Sobre-escritura del % renta (para negociar por encima o por debajo del auto).
  const [pctOverride, setPctOverride] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/rent-reduction/service-catalog/all')
      .then((r) => setCatalog(r.data || []))
      .catch(() => toast.error('No se pudo cargar el catálogo'))
      .finally(() => setLoading(false));
  }, []);

  const toggle = (svc) => {
    setSelected((s) => {
      const n = { ...s };
      if (n[svc.id]) delete n[svc.id];
      else n[svc.id] = { ...svc };
      return n;
    });
  };

  const updateValue = (id, val) => {
    setSelected((s) => ({ ...s, [id]: { ...s[id], market_value_eur: Number(val) || 0 } }));
  };

  const items = useMemo(() => Object.values(selected), [selected]);
  const annualTotal = useMemo(
    () => items.reduce((sum, it) => sum + (Number(it.market_value_eur) || 0) * (12 / (it.frequency_months || 12)), 0),
    [items],
  );
  const equivMonthly = annualTotal / 12;
  const autoPct = neg.current_rent_monthly ? (equivMonthly / neg.current_rent_monthly * 100) : 0;
  const reductionPct = pctOverride ?? autoPct;

  // Años hasta fin de contrato (respeta el override manual si el usuario lo cambió).
  const yearsToEnd = duration;

  // Ahorro total efectivo hasta fin de contrato — respetando la frecuencia real
  // de cada servicio (los que se hacen cada 3 años sólo cuentan las ejecuciones
  // que caben en el periodo).
  const totalUntilEnd = useMemo(
    () => items.reduce((sum, it) => {
      const val = Number(it.market_value_eur) || 0;
      const freq = (it.frequency_months || 12) / 12;
      const executions = Math.floor(yearsToEnd / freq) + (yearsToEnd % freq >= freq / 2 ? 1 : 0);
      return sum + val * executions;
    }, 0),
    [items, yearsToEnd],
  );

  // Ahorro real que TOTEM se apunta si consigue la bajada de renta:
  //   annualReduction = renta actual anual × %reducción / 100
  //   ahorroTotem = annualReduction × años restantes de contrato
  // Importe a abonar = coste real de los servicios in-kind (totalUntilEnd)
  const currentAnnualRent = toAnnual(neg.current_rent_monthly);
  const annualReduction = currentAnnualRent * (reductionPct / 100);
  const ahorroTotem = annualReduction * yearsToEnd;
  const importeAbonar = totalUntilEnd;
  const netoTotem = ahorroTotem - importeAbonar;

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/rent-reduction/${neg.id}/service-offer`, {
        items: items.map((it) => ({
          id: it.id, title: it.title,
          market_value_eur: Number(it.market_value_eur) || 0,
          frequency_months: Number(it.frequency_months) || 12,
          site_env: it.site_env || 'urbano',
          type: it.type || 'inspeccion_general',
        })),
        annual_total_eur: Math.round(annualTotal * 100) / 100,
        equivalent_reduction_monthly_eur: Math.round(equivMonthly * 100) / 100,
        duration_years: Number(duration) || 1,
        notes: pctOverride !== null ? `override_pct=${pctOverride.toFixed(2)}` : '',
      });
      // Si el usuario forzó un % distinto al auto, actualizamos también el
      // target_rent_monthly de la negociación para reflejar la nueva propuesta.
      if (pctOverride !== null && neg.current_rent_monthly) {
        const newTargetMonthly = neg.current_rent_monthly * (1 - pctOverride / 100);
        await api.patch(`/rent-reduction/${neg.id}`, {
          target_rent_monthly: Math.round(newTargetMonthly * 100) / 100,
        });
      }
      toast.success('Oferta guardada');
      onSaved(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error guardando');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[10060] bg-black/90 backdrop-blur flex items-center justify-center p-4"
      onClick={onClose} data-testid="service-offer-builder">
      <div onClick={(e) => e.stopPropagation()}
        className="bg-[#0a0a0a] border border-orange-500/30 rounded-3xl w-full max-w-3xl max-h-[92vh] overflow-y-auto p-5 space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-[10px] font-black tracking-widest text-orange-400">DISEÑAR OFERTA IN-KIND</div>
            <div className="text-white font-black text-lg mt-1 flex items-center gap-2">
              <Hammer size={18} className="text-orange-400"/> Servicios preventivos como palanca
            </div>
            <div className="text-white/50 text-xs mt-1">
              Selecciona los servicios que ofrecemos ejecutar sobre la propiedad. Su valor de mercado se compensa como bajada mensual de renta.
            </div>
          </div>
          <button onClick={onClose} className="text-white/40 hover:text-white p-1"><XCircle size={22}/></button>
        </div>

        {loading ? (
          <div className="py-12 flex justify-center"><Loader2 className="animate-spin text-orange-400" size={26}/></div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {catalog.map((svc) => {
                const sel = selected[svc.id];
                return (
                  <div key={svc.id}
                    className={`border rounded-xl p-3 cursor-pointer transition-colors ${sel ? 'bg-orange-500/10 border-orange-500/50' : 'bg-white/5 border-white/10 hover:border-white/25'}`}
                    onClick={() => toggle(svc)}
                    data-testid={`svc-${svc.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="text-white font-bold text-xs">{svc.title}</div>
                        <div className="text-white/50 text-[11px] mt-0.5">{svc.description}</div>
                        <div className="text-[10px] text-white/40 mt-1">
                          Entorno {svc.site_env} · cada {svc.frequency_months >= 12 && svc.frequency_months % 12 === 0 ? `${svc.frequency_months / 12} año${svc.frequency_months / 12 > 1 ? 's' : ''}` : `${svc.frequency_months}m`}
                        </div>
                      </div>
                      <input type="checkbox" checked={!!sel} readOnly
                        className="w-4 h-4 accent-orange-400 flex-shrink-0 mt-1"/>
                    </div>
                    {sel && (
                      <div className="mt-2 pt-2 border-t border-white/10 flex items-center gap-2 flex-wrap" onClick={(e) => e.stopPropagation()}>
                        <span className="text-[10px] text-white/50">Valor €:</span>
                        <input type="number" value={sel.market_value_eur}
                          onChange={(e) => updateValue(svc.id, e.target.value)}
                          className="w-24 bg-black/40 border border-white/10 rounded px-2 py-1 text-white text-xs"
                          data-testid={`svc-value-${svc.id}`}/>
                        {svc.frequency_months > 12 && (() => {
                          const unit = Number(sel.market_value_eur) || 0;
                          const freqYears = svc.frequency_months / 12;
                          const execs = Math.floor(yearsToEnd / freqYears) + (yearsToEnd % freqYears >= freqYears / 2 ? 1 : 0);
                          const totalPeriod = unit * execs;
                          const annualEq = unit / freqYears;
                          return (
                            <div className="text-[10px] text-white/60 flex items-center gap-2 ml-auto">
                              <span title={`${execs} ejec. en ${yearsToEnd.toFixed(1)}a`}>
                                Total <b className="text-orange-300">{totalPeriod.toLocaleString('es-ES',{maximumFractionDigits:0})} €</b>
                              </span>
                              <span>·</span>
                              <span>
                                <b className="text-white">{annualEq.toLocaleString('es-ES',{maximumFractionDigits:0})} €/año</b>
                              </span>
                            </div>
                          );
                        })()}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="bg-orange-500/10 border border-orange-500/30 rounded-xl p-3">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-center">
                <MiniStat label="Servicios" value={items.length}/>
                <MiniStat label="Valor anual eq." value={`${annualTotal.toLocaleString('es-ES', {maximumFractionDigits:0})} €`}/>
                <MiniStat label="Equiv. anual" value={`${(annualTotal).toLocaleString('es-ES', {maximumFractionDigits:0})} €/año`} accent/>
                {/* % renta EDITABLE */}
                <div className="bg-black/40 rounded-lg py-1.5 px-2">
                  <div className="text-[9px] uppercase tracking-widest text-white/50">% renta</div>
                  <div className="flex items-center justify-center gap-1">
                    <input type="number" step="0.1" min="0" max="100"
                      value={Number(reductionPct.toFixed(1))}
                      onChange={(e) => setPctOverride(Number(e.target.value))}
                      data-testid="input-pct-override"
                      className="w-16 bg-transparent border-b border-orange-400/40 text-center text-orange-300 font-black text-sm focus:outline-none focus:border-orange-400"/>
                    <span className="text-orange-300 font-black text-sm">%</span>
                  </div>
                  {pctOverride !== null && Math.abs(pctOverride - autoPct) > 0.05 && (
                    <button onClick={() => setPctOverride(null)}
                      className="text-[9px] text-white/40 hover:text-white underline mt-0.5"
                      title={`Auto: ${autoPct.toFixed(1)}%`}>
                      reset auto
                    </button>
                  )}
                </div>
                <MiniStat label={neg.contract_end_date && durationOverride === null ? `Hasta fin (${yearsToEnd.toFixed(1)}a)` : `${yearsToEnd.toFixed(1)}a`}
                          value={`${totalUntilEnd.toLocaleString('es-ES', {maximumFractionDigits:0})} €`} accent/>
              </div>
              <div className="flex items-center gap-2 mt-3 justify-center flex-wrap">
                <span className="text-[11px] text-white/60">Duración del acuerdo:</span>
                {durationOverride === null && neg.contract_end_date ? (
                  <>
                    <span className="text-white font-black text-xs bg-white/10 border border-white/20 rounded px-2 py-1">
                      Auto · {autoYears.toFixed(1)} años (hasta {String(neg.contract_end_date).slice(0,10)})
                    </span>
                    <button onClick={() => setDurationOverride(Math.round(autoYears))}
                      data-testid="btn-override-duration"
                      className="text-[10px] text-white/50 hover:text-white underline">
                      cambiar
                    </button>
                  </>
                ) : (
                  <>
                    <input type="number" min="0.5" max="20" step="0.5"
                      value={duration}
                      onChange={(e) => setDurationOverride(Number(e.target.value))}
                      data-testid="svc-duration"
                      className="w-20 bg-black/40 border border-white/10 rounded px-2 py-1 text-white text-xs"/>
                    <span className="text-white/50 text-[11px]">años</span>
                    {neg.contract_end_date && (
                      <button onClick={() => setDurationOverride(null)}
                        className="text-[10px] text-white/50 hover:text-white underline">
                        volver a auto
                      </button>
                    )}
                  </>
                )}
              </div>
              <div className="mt-2 text-[11px] text-white/60 text-center">
                Sobre renta actual de <b className="text-white">{eur0(toAnnual(neg.current_rent_monthly))} €/año</b>, la equivalencia in-kind supone una <b className="text-orange-300">bajada del {reductionPct.toLocaleString('es-ES', {minimumFractionDigits:1, maximumFractionDigits:1})}%</b>{pctOverride !== null && ` (auto: ${autoPct.toFixed(1)}%)`}.
                {totalUntilEnd > 0 && (
                  <> Ahorro efectivo{neg.contract_end_date && durationOverride === null ? ' hasta fin de contrato' : ''}: <b className="text-[#A3FF12]">{totalUntilEnd.toLocaleString('es-ES', {maximumFractionDigits:0})} €</b> ({yearsToEnd.toFixed(1)} años).</>
                )}
              </div>

              {/* Balance TOTEM: ahorro por bajada vs coste de servicios */}
              <div className="mt-3 pt-3 border-t border-orange-400/20 grid grid-cols-1 md:grid-cols-3 gap-2">
                <div className="bg-black/40 border border-[#A3FF12]/30 rounded-lg p-2.5 text-center">
                  <div className="text-[9px] uppercase tracking-widest text-white/50">Ahorro TOTEM</div>
                  <div className="text-[#A3FF12] font-black text-lg">{ahorroTotem.toLocaleString('es-ES',{maximumFractionDigits:0})} €</div>
                  <div className="text-[10px] text-white/40">{annualReduction.toLocaleString('es-ES',{maximumFractionDigits:0})} €/año × {yearsToEnd.toFixed(1)} años</div>
                </div>
                <div className="bg-black/40 border border-orange-400/40 rounded-lg p-2.5 text-center">
                  <div className="text-[9px] uppercase tracking-widest text-white/50">Importe a abonar</div>
                  <div className="text-orange-300 font-black text-lg">{importeAbonar.toLocaleString('es-ES',{maximumFractionDigits:0})} €</div>
                  <div className="text-[10px] text-white/40">Servicios in-kind hasta fin contrato</div>
                </div>
                <div className={`bg-black/40 border rounded-lg p-2.5 text-center ${netoTotem >= 0 ? 'border-[#A3FF12]/50' : 'border-red-400/50'}`}>
                  <div className="text-[9px] uppercase tracking-widest text-white/50">Neto TOTEM</div>
                  <div className={`font-black text-lg ${netoTotem >= 0 ? 'text-[#A3FF12]' : 'text-red-400'}`}>
                    {netoTotem >= 0 ? '+' : ''}{netoTotem.toLocaleString('es-ES',{maximumFractionDigits:0})} €
                  </div>
                  <div className="text-[10px] text-white/40">Ahorro − Servicios</div>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <button onClick={onClose} className="px-4 py-2 border border-white/10 rounded-xl text-white/70 text-sm">Cancelar</button>
              <button onClick={save} disabled={saving || items.length === 0}
                data-testid="btn-save-service-offer"
                className="bg-orange-500 text-black font-black text-sm px-4 py-2 rounded-xl hover:bg-orange-400 disabled:opacity-40 flex items-center gap-2">
                {saving ? <Loader2 className="animate-spin" size={14}/> : <Save size={14}/>}
                {saving ? 'Guardando…' : 'Guardar oferta'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const MiniStat = ({ label, value, accent }) => (
  <div className={`rounded-lg p-2 ${accent ? 'bg-[#A3FF12]/15 border border-[#A3FF12]/40' : 'bg-white/5'}`}>
    <div className="text-[9px] uppercase text-white/50 font-bold">{label}</div>
    <div className={`text-sm font-black ${accent ? 'text-[#A3FF12]' : 'text-white'}`}>{value}</div>
  </div>
);

const BtnAction = ({ onClick, loading, kind = 'primary', children }) => {
  const cls = kind === 'success' ? 'bg-[#A3FF12] text-black hover:bg-[#b8ff3a]'
            : kind === 'danger'  ? 'bg-red-500 text-white hover:bg-red-400'
            : 'bg-white/10 text-white hover:bg-white/20';
  return (
    <button onClick={onClick} disabled={loading}
      className={`${cls} font-black text-xs px-3 py-1.5 rounded-lg disabled:opacity-40 flex items-center gap-1`}>
      {loading ? <Loader2 className="animate-spin" size={11}/> : null}
      {children}
    </button>
  );
};

export default BajadaRenta;
